import { ColDef } from "@ag-grid-community/all-modules";
import { AgGridReact } from "@ag-grid-community/react";
import * as _ from "lodash";
import AdminSiteLink from "admin/common/AdminSiteLink";
import columnBuilder from "common/agGrid/columnBuilder";
import defaultGridOptions from "common/agGrid/defaultGridOptions";
import RenderParameters from "common/agGrid/renderParameters";
import Loading from "common/ui/Loading";
import useStartup from "common/ui/useStartup";
import Merchant from "domain/merchant";
import React, { ChangeEvent, useState } from "react";
import { Breadcrumb, Button, Form } from "react-bootstrap";
import AddMerchantModal from "./AddMerchantModal";
import AddMerchantRequest from "./addMerchantRequest";
import merchantService from "./merchantService";
import ValidationError from "common/validationError";
import EditMerchantModal from "./EditMerchantModal";
import usePermission from "common/security/usePermission";
import adminPermissions from "admin/app/adminPermissions";
import Fcm from "domain/fcm";
import fcmService from "./general/fcmService";
import ConfirmationModal, { useConfirmationModal } from "common/ui/ConfirmationModal";
import systemConfigService from "features/systemConfig/systemConfigService";
import SystemConfig from "domain/systemConfig";
import "./Merchants.scss";
import { getErpSystemName } from "domain/erpSystem";
import moment from "moment";

const MerchantsPage: React.FC<any> = () => {

    const [merchants, setMerchants] = useState<Merchant[]>(null);
    const [systemConfig, setSystemConfig] = useState<SystemConfig>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>(null);
    const [editingMerchant, setEditingMerchant] = useState<Merchant>(null);
    const [fcms, setFcms] = useState<Fcm[]>(null);
    const [typingSearchText, setTypingSearchText] = useState("");
    
    const canUpdateMerchants = usePermission(adminPermissions.UpdateMerchant);
    const confirmModal = useConfirmationModal();
    
    useStartup(() => {
        systemConfigService.getSystemConfig()
            .then(s => setSystemConfig(s));
        merchantService.listMerchants()
            .then(m => setMerchants(_.orderBy(m, m => m.name)));
        fcmService.listFcms()
            .then(m => setFcms(m));
    });

    function listColumnDefinitions(): ColDef[] {
        return columnBuilder.collection(builder => [
            builder.for("Name", "name")
                .withCellRenderer((params: RenderParameters<Merchant>) => 
                    <AdminSiteLink.MerchantDetails merchantId={params.node.data.id}>
                      {params.value}
                    </AdminSiteLink.MerchantDetails>
                ),
            builder.for("Auto Offer", "enableAutoOffer")
            .withCellRenderer((params: RenderParameters<Merchant>) =>  {
                return params.value ? <i className="text-success fa fa-check-circle"></i> : <i className="text-danger fa fa-times-circle"></i>
            }),
            builder.for("Auto Position", "enableAutoPosition")
            .withCellRenderer((params: RenderParameters<Merchant>) =>  {
                return params.value ? <i className="text-success fa fa-check-circle"></i> : <i className="text-danger fa fa-times-circle"></i>
            }),
            builder.for("EOD (CST)", "endOfDay").withFormatter(m => moment(m, "HH:mm:ss").format("hh:mm A")),
            builder.for("ERP System", "erpSystem")
                .withCellRenderer((params: RenderParameters<Merchant>) =>  {
                return params.node.data.hasErpIntegration ? <span>{getErpSystemName(params.value)}</span> : null
            }),
            builder.for("Edit Info", "")
                .withCellRenderer((params: RenderParameters<Merchant>) => {
                    if(canUpdateMerchants) {
                        return <Button variant="link" className="p-0"
                            onClick={() => setEditingMerchant(params.node.data)}>
                            <i className="fa fa-pencil-square-o"></i>
                        </Button>
                    }
                    else return null;
                })
        ])
    }

    function addNew() {
        setShowAddModal(true);
    }

    async function addMerchant(request: AddMerchantRequest) {
        try {
            const newMerchant = await merchantService.createMerchant(request);
            setMerchants([newMerchant, ...merchants]);
            setShowAddModal(false);
        }
        catch (error) {
            setValidationErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    async function updateMerchant(merchant: Merchant) {
        try {
            await merchantService.updateMerchant(merchant.id, { 
                merchantName: merchant.name, 
                merchantEmail: merchant.email,
                merchantPhone: merchant.phone,
                merchantWebsiteUrl: merchant.websiteUrl,
                mailingAddress: merchant.mailingAddress 
            });
            const updatedMerchants = [...merchants];
            setMerchants(updatedMerchants.map(m => m.id === merchant.id ? merchant : m));
            setEditingMerchant(null);
            setValidationErrors([]);
        }
        catch (error) {
            setValidationErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    function handleChangeMasterAutoOffer(isEnabled: boolean) {
        confirmModal.show(
            `${(isEnabled ? "Enable" : "Disable")} Master Auto Offer?`,
            isEnabled ?
            <>Turning <strong>ON</strong> Auto Offer will enable Auto Offer for all Merchants who had Auto Offer ON</>:
            <>Turning <strong>OFF</strong> Auto Offer will disable Auto Offer for all Merchants who had it ON</>,
            () => {
                systemConfigService.updateSystemConfig(isEnabled);
                setSystemConfig({...systemConfig, enableMasterAutoOffer: isEnabled});
            }
        );
    }

    function handleChangeMasterAutoPosition(isEnabled: boolean) {
        confirmModal.show(
            `${(isEnabled ? "Enable" : "Disable")} Master Auto Position?`,
            isEnabled ?
            <>Turning <strong>ON</strong> Auto Position will enable Auto Position for all Merchants who had Auto Position ON and will cause all Net Positions to automatically hedge</> :
            <>Turning <strong>OFF</strong> Auto Position will disable Auto Position for all Merchants who had it ON</>,
            () => {
                systemConfigService.updateSystemConfig(null, isEnabled);
                setSystemConfig({...systemConfig, enableMasterAutoPosition: isEnabled});
            }
        );
    }
    
    return <div>
        <div className="row col">
            <Breadcrumb>
                <li className="breadcrumb-item"><AdminSiteLink.Home>Admin</AdminSiteLink.Home></li>
                <Breadcrumb.Item active>Merchants</Breadcrumb.Item>
            </Breadcrumb>
            <div className="form form-inline search-container ml-auto">
                <div className="form-group search-row" style={{ marginBottom: "0" }}>
                    <span className="fa fa-search search-icon-container"></span>

                    <Form.Control type="text"
                    placeholder="Search"
                    id="search"
                    value={typingSearchText}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setTypingSearchText(event.target.value);
                    }} />
                </div>
            </div>
        </div>

        <Loading isLoading={merchants == null || fcms == null}>
        {() => 
        <div className="container-fluid">
            <div className="row table-header-row">
                <div className="col">
                    <div className="page-sub-header">Merchants</div>
                </div>
                <div className="col action-bar">
                    <label className="pr-1 master-switch">Master Auto Offer</label>
                    <Form.Check
                        type="switch" 
                        id="master-auto-offer-switch"
                        className="d-inline-block mr-2" 
                        checked={systemConfig?.enableMasterAutoOffer}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => { handleChangeMasterAutoOffer(event.target.checked) }} 
                    />
                    <label className="pr-1 master-switch">Master Auto Position</label>
                    <Form.Check 
                        type="switch" 
                        id="master-auto-position-switch" 
                        label="" 
                        className="d-inline-block"  
                        checked={systemConfig?.enableMasterAutoPosition}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => { handleChangeMasterAutoPosition(event.target.checked) }} 
                    />
                    <Button onClick={addNew} variant="link">
                        <i className="material-icons">add_circle_outline</i> New Merchant
                    </Button>
                </div>
            </div>
            <div className="row">
                <div className="ag-theme-alpine" style={{ width: "100%" }} >
                    <AgGridReact
                        {...defaultGridOptions}
                        columnDefs={listColumnDefinitions()}
                        paginationPageSize= { 50 }
                        pagination
                        rowData={merchants} 
                        getRowId={params => params.data.id}
                        domLayout="autoHeight"
                        onGridReady={params => params.api.sizeColumnsToFit()}
                        quickFilterText={typingSearchText}
                    />
                </div>
            </div>
        </div>}
        </Loading>

        {showAddModal && <AddMerchantModal
            fcms={fcms}
            onSave={addMerchant}
            onHide={() => { setShowAddModal(false); setValidationErrors(null) }}
            validationErrors={validationErrors}
        />}
        {editingMerchant && canUpdateMerchants && <EditMerchantModal
            merchant={editingMerchant}
            validationErrors={validationErrors}
            onSave={updateMerchant}
            onHide={() => {
                setEditingMerchant(null);
                setValidationErrors([]);
            }} />}
        <ConfirmationModal modalState={confirmModal.state} />
    </div>
};
 
export default MerchantsPage;